import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, readToken } from '../api/client.js';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
const ALLOWED = new Set(ACCEPT.split(','));
const MAX_FILE_MB = 20;
const MAX_FILES = 10;

async function uploadAsset(postId, file, ordinal) {
  const res = await fetch(`/api/posts/${encodeURIComponent(postId)}/assets/${ordinal}`, {
    method: 'POST',
    headers: { 'Content-Type': file.type, Authorization: `Bearer ${readToken()}` },
    body: file,
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b.error || `upload failed (${res.status})`);
  }
  return res.json();
}

/**
 * Upload-your-own-content modal (migration 030 BYO). Creates a user_upload post,
 * uploads the image(s), finalizes through the caption safety gate, then routes
 * to the post detail where the existing approve/publish UI takes over.
 */
export function UploadPostModal({ defaultProjectKey, onClose }) {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [projectKey, setProjectKey] = useState(defaultProjectKey || '');
  const [title, setTitle] = useState('');
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const projectsQ = useQuery({ queryKey: ['projects-for-upload'], queryFn: () => apiGet('/projects') });
  const projects = projectsQ.data?.projects || [];

  function addFiles(list) {
    setError(null);
    const next = [];
    for (const f of Array.from(list || [])) {
      if (!ALLOWED.has(f.type)) { setError(`${f.name}: only images are supported (video coming soon).`); continue; }
      if (f.size > MAX_FILE_MB * 1024 * 1024) { setError(`${f.name}: over ${MAX_FILE_MB} MB.`); continue; }
      next.push(f);
    }
    setFiles((prev) => [...prev, ...next].slice(0, MAX_FILES));
  }

  async function submit() {
    setError(null);
    if (!projectKey) { setError('Pick a project.'); return; }
    if (!files.length) { setError('Add at least one image.'); return; }
    setBusy(true);
    try {
      const tags = hashtags.split(/[\s,]+/).map((t) => t.trim()).filter(Boolean);
      const { post_id } = await apiPost('/posts/upload', { project_key: projectKey, title, caption, hashtags: tags });
      for (let i = 0; i < files.length; i++) await uploadAsset(post_id, files[i], i);
      const fin = await apiPost(`/posts/${post_id}/upload-finalize`, {});
      navigate(`/posts/${post_id}`);
      onClose?.();
      return fin;
    } catch (err) {
      setError(err.message || 'Upload failed.');
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="section-h-row">
          <h2>Upload your own post</h2>
          <button className="btn-icon" onClick={onClose} aria-label="Close">×</button>
        </div>
        <p className="muted small">Publish content you made yourself (your own image or carousel) through your connected accounts. Video upload is coming soon.</p>

        <label className="field-label">Project</label>
        <select className="select" value={projectKey} onChange={(e) => setProjectKey(e.target.value)} disabled={busy}>
          <option value="">Select a project…</option>
          {projects.map((p) => <option key={p.key} value={p.key}>{p.display_name || p.key}</option>)}
        </select>

        <label className="field-label">Title (internal)</label>
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. CliLancers promo" disabled={busy} />

        <label className="field-label">Caption</label>
        <textarea className="textarea" rows={4} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="The caption that will be posted." disabled={busy} />

        <label className="field-label">Hashtags</label>
        <input className="input" value={hashtags} onChange={(e) => setHashtags(e.target.value)} placeholder="#freelance #invoicing (space or comma separated)" disabled={busy} />

        <label className="field-label">Images ({files.length}/{MAX_FILES})</label>
        <div className="sample-drop-zone" role="button" tabIndex={0}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}>
          <input ref={fileRef} type="file" accept={ACCEPT} multiple style={{ display: 'none' }}
            onChange={(e) => addFiles(e.target.files)} />
          <span className="muted">Click or drag images here — up to {MAX_FILES}, {MAX_FILE_MB} MB each</span>
        </div>
        {files.length > 0 && (
          <ul className="upload-file-list">
            {files.map((f, i) => (
              <li key={i}>
                <span>{f.name}</span>
                <button className="sample-delete" disabled={busy}
                  onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}>×</button>
              </li>
            ))}
          </ul>
        )}

        {error && <div className="error sample-error">{error}</div>}

        <div className="modal-actions">
          <button className="btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={busy || !files.length || !projectKey}>
            {busy ? 'Uploading…' : 'Upload & send to review'}
          </button>
        </div>
      </div>
    </div>
  );
}
